import { isNumberSize } from "./itemSize";
import { rateNumber } from "./processRate";
import {
  CuttingPieceProps,
  CuttingRateSource,
  ICuttingLineInput,
} from "../types/cuttingOrder";
import { ICuttingReceiveLineInput } from "../types/cuttingReceive";

// Helpers shared by cutting orders and cutting receives. Dates, qty, amounts, party names and the Open/Close states come
// from materialLine.ts.

// An order line's key: one per item and size ("12:3", "12:0" for an item without sizes). Keep in step with
// CuttingLines::key on the backend.
export const optionKey = (itemId: number, sizeId: number | null | undefined) =>
  `${itemId}:${sizeId || 0}`;

export const cuttingLineKey = (line: {
  item_id: number;
  size_id: number | null;
}) => optionKey(line.item_id, line.size_id);

// "AB-01 (94)", or the code alone for an item without sizes. With the size unit a number size reads "AB-01 (94mm)" (a
// size saved as text before 2026-10-25 stays as it is written), like CuttingLines::lineName on the backend.
export const lineName = (
  code: string | null | undefined,
  sizeName: string | null | undefined,
  unit = ""
) =>
  sizeName
    ? `${code || "-"} (${sizeName}${isNumberSize(sizeName) ? unit : ""})`
    : code || "-";

export const roundLength = (value: number) => Math.round(value * 100) / 100;

// A length in the size unit, like the material sizes show: "28,800mm" or "1,540.5mm".
export const formatLength = (
  value: number | string | null | undefined,
  unit: string
) =>
  `${Number(value || 0).toLocaleString("en-IN", {
    maximumFractionDigits: 2,
  })}${unit}`;

// The pieces an order line cuts: what was received once a receive closed it, else its qty, or more if more came.
export const effectiveQty = (qty: number, received: number, closed: boolean) =>
  closed ? received : Math.max(qty, received);

// Cutting maths runs in hundredths of the size unit, like CuttingStock on the backend, so both sides split alike.
export const toUnits = (length: number | string | null | undefined) =>
  Math.round(Number(length || 0) * 100);

export const fromUnits = (units: number) => Math.round(units) / 100;

// Pieces by length in units, and the same as the screens list them: longest first.
export type CuttingPool = Map<number, number>;

export const poolOf = (pieces: CuttingPieceProps[]): CuttingPool => {
  const pool: CuttingPool = new Map();

  pieces.forEach((piece) =>
    pool.set(
      toUnits(piece.length),
      (pool.get(toUnits(piece.length)) || 0) + piece.count
    )
  );

  return pool;
};

export const piecesOf = (
  pool: CuttingPool,
  bars: Set<number>
): CuttingPieceProps[] =>
  Array.from(pool.entries())
    .filter(([, count]) => count > 0)
    .sort(([a], [b]) => b - a)
    .map(([units, count]) => ({
      length: fromUnits(units),
      count,
      bar: bars.has(units),
    }));

// One piece's length as the bar sizes show it, without grouping: "3600mm", "1104mm", "122.5mm".
export const pieceLength = (value: number, unit: string) =>
  `${Number(value || 0).toLocaleString("en-IN", {
    maximumFractionDigits: 2,
    useGrouping: false,
  })}${unit}`;

// "3600mm બાર × 2", "1104mm × 1": an offcut is the length alone (the user had "ટુકડો" removed). Without barWord a bar
// is too, "3600mm × 2" (the order detail page, where the user had "બાર" removed).
export const pieceLabel = (
  piece: CuttingPieceProps,
  unit: string,
  barWord = true
) =>
  `${pieceLength(piece.length, unit)}${piece.bar && barWord ? " બાર" : ""} × ${
    piece.count
  }`;

export type CuttingDemand<R> = {
  // The cut length and its scrap limit in units: a leftover shorter than scrap is scrap, a longer one an offcut.
  cut: number;
  qty: number;
  scrap: number;
  ref: R;
};

export type CuttingCut<R> = {
  from: number;
  kind: "bar" | "offcut" | "extra";
  count: number;
  demand: number;
  qty: number;
  left: number;
  scrap: boolean;
  ref: R;
};

// What planCutting returns: the pieces left, the runs of identical cuts, the units scrapped, the pieces short per demand
// and the extra bars opened.
export type CuttingPlan<R> = {
  pool: CuttingPool;
  rows: CuttingCut<R>[];
  scrap: number;
  short: number[];
  opened: number;
};

// The smallest piece that gives all `remaining` pieces of `cut`, else the smallest that gives any: offcuts first, then
// bars. Null when none does.
const pickPiece = (
  pool: CuttingPool,
  bars: Set<number>,
  cut: number,
  remaining: number
) => {
  const lengths = Array.from(pool.keys()).sort((a, b) => a - b);

  for (const bar of [false, true]) {
    let fits: number | null = null;

    for (const length of lengths) {
      if (
        (pool.get(length) || 0) <= 0 ||
        length < cut ||
        bars.has(length) !== bar
      ) {
        continue;
      }

      if (Math.floor(length / cut) >= remaining) return length;

      if (fits === null) fits = length;
    }

    if (fits !== null) return fits;
  }

  return null;
};

// Cuts the demands, in the order given, from the pieces in `start` (units). For each it takes offcuts before bars
// (pieces as long as one of `bars`), and bars of `extraBar` (as many as needed, for the suggestion) only after both.
// Within those it takes the smallest piece that gives all the pieces still needed, else the smallest that gives any,
// as many of those at once as are used whole. A leftover shorter than the demand's scrap is scrap; a longer one goes
// back as an offcut. Returns the pieces left, the runs of identical cuts, the units scrapped, the pieces no piece was
// left for (per demand) and the extra bars used. Keep in step with CuttingStock::plan on the backend.
export const planCutting = <R>(
  start: CuttingPool,
  bars: Set<number>,
  demands: CuttingDemand<R>[],
  extraBar?: number
): CuttingPlan<R> => {
  const pool: CuttingPool = new Map(start);
  const rows: CuttingCut<R>[] = [];
  const short: number[] = [];
  let scrap = 0;
  let opened = 0;

  demands.forEach((demand, index) => {
    const cut = demand.cut;
    let remaining = Math.max(0, demand.qty);
    short[index] = 0;

    if (cut <= 0) return;

    while (remaining > 0) {
      let from = pickPiece(pool, bars, cut, remaining);
      let kind: CuttingCut<R>["kind"];
      let available: number;

      if (from !== null) {
        kind = bars.has(from) ? "bar" : "offcut";
        available = pool.get(from) || 0;
      } else if (extraBar !== undefined && extraBar >= cut) {
        from = extraBar;
        kind = "extra";
        available = Number.MAX_SAFE_INTEGER;
      } else {
        short[index] += remaining;
        break;
      }

      const each = Math.floor(from / cut);
      const count =
        each >= remaining
          ? 1
          : Math.min(available, Math.floor(remaining / each));
      const qty = Math.min(each, remaining);
      const left = from - qty * cut;
      const isScrap = left > 0 && left < demand.scrap;

      if (kind === "extra") {
        opened += count;
      } else {
        const rest = (pool.get(from) || 0) - count;

        if (rest > 0) pool.set(from, rest);
        else pool.delete(from);
      }

      if (isScrap) {
        scrap += left * count;
      } else if (left > 0) {
        pool.set(left, (pool.get(left) || 0) + count);
      }

      remaining -= count * qty;

      const last = rows[rows.length - 1];

      if (
        last &&
        last.from === from &&
        last.kind === kind &&
        last.demand === index &&
        last.qty === qty &&
        last.left === left
      ) {
        last.count += count;
      } else {
        rows.push({
          from,
          kind,
          count,
          demand: index,
          qty,
          left,
          scrap: isScrap,
          ref: demand.ref,
        });
      }
    }
  });

  return { pool, rows, scrap, short, opened };
};

// Whether a plan left any piece of the demands from `own` on uncut.
const shortFrom = (short: number[], own: number) =>
  short.slice(own).some((value) => value > 0);

// The fewest bars of `bar` (units) that, added to the pieces in `start`, cut every piece of the demands from `own` on:
// the order's own lines. The owed pieces of older orders before them are cut first but may stay short, as
// CuttingStock::arrive counts it. Null when no number of them does: a piece longer than the bar that nothing held fits.
// It starts from the bars planCutting opens after the pieces, then checks the plan with the bars among the pieces, as
// the backend cuts them once the order is saved.
export const barsNeeded = <R>(
  start: CuttingPool,
  bars: Set<number>,
  demands: CuttingDemand<R>[],
  own: number,
  bar: number
): number | null => {
  const extra = planCutting(start, bars, demands, bar);

  if (shortFrom(extra.short, own)) return null;

  const shortWith = (count: number) => {
    const pool: CuttingPool = new Map(start);

    if (count > 0) pool.set(bar, (pool.get(bar) || 0) + count);

    return shortFrom(planCutting(pool, bars, demands).short, own);
  };

  const limit =
    extra.opened +
    demands
      .slice(own)
      .reduce((sum, demand) => sum + Math.max(0, demand.qty), 0);
  let count = extra.opened;

  while (shortWith(count)) {
    if (count >= limit) return null;
    count += 1;
  }

  while (count > 0 && !shortWith(count - 1)) count -= 1;

  return count;
};

export const rateSourceLabel = (source: CuttingRateSource | undefined) =>
  source === 1
    ? "પાર્ટી ભાવ"
    : source === 2
    ? "ભાવ નથી"
    : source === 3
    ? "બદલેલ ભાવ"
    : "આઈટમ ભાવ";

export const lineAmount = (qty: number, rate: number) =>
  Math.round(qty * rate * 100) / 100;

// The order's lines as the API takes them. Only a user who may see rates sends the rate. refresh asks to copy the
// item's cut length and rate again.
export const orderLinePayload = (
  lines: ICuttingLineInput[],
  withRates: boolean
) =>
  lines.map((line) => ({
    item_id: line.item_id,
    size_id: line.size_id,
    qty: Number(line.qty),
    ...(withRates ? { rate: rateNumber(line.rate) } : {}),
    ...(line.refresh ? { refresh: true } : {}),
  }));

// The receive's lines as the API takes them. Only a user who may see rates sends the rate.
export const receiveLinePayload = (
  lines: ICuttingReceiveLineInput[],
  withRates: boolean
) =>
  lines.map((line) => ({
    order_item_id: line.order_item_id,
    qty: Number(line.qty),
    close_order: line.close_order,
    ...(withRates ? { rate: rateNumber(line.rate) } : {}),
  }));

// Whether a receive's pieces make up their order line, with what came on other receives, or more.
export const fillsOrderLine = (
  orderQty: number,
  received: number,
  qty: string
) => Number(qty) > 0 && received + Number(qty) >= orderQty;

// "ઓર્ડર બંધ" once a receive line's qty changes (user, 2026-10-31): ticked when the qty comes to make up the order line,
// unticked when it no longer does; otherwise as it was, so a line closed short keeps its tick.
export const closeAfterQty = (
  orderQty: number,
  received: number,
  previous: string,
  qty: string,
  closeOrder: boolean
) => {
  const fills = fillsOrderLine(orderQty, received, qty);

  return fills !== fillsOrderLine(orderQty, received, previous)
    ? fills
    : closeOrder;
};
